import { useState } from 'react';
import { useDealerSecurityGroupsQuery } from '@/features/security-admin/hooks/useDealerSecurityGroupsQuery';
import { PermissionGuard } from '@/features/auth';
import { Alert, Button } from '@shared/components';
import { DealerSecurityGroupDataTable } from './DealerSecurityGroupDataTable';
import { AttachDealerToGroupPanel } from './AttachDealerToGroupPanel';
import type { BackRef } from '@/features/smr-admin/types/backRef';

interface DealerSecurityGroupsProps {
  dealerId: string;
  /**
   * Dealer name supplied by the parent (`DealerDetail`) which already has
   * the dealer loaded. Passed as a prop instead of re-fetching here so the
   * `backRef` is always defined synchronously on the first render of the
   * DataTable — otherwise the groups query can resolve before a duplicate
   * `useDealerQuery` here, leaving `backRef={undefined}` for the brief
   * window in which a user could click View and lose the back-nav context.
   */
  dealerName: string;
}

export function DealerSecurityGroups({ dealerId, dealerName }: DealerSecurityGroupsProps) {
  const [attaching, setAttaching] = useState(false);
  const groupsQuery = useDealerSecurityGroupsQuery(dealerId);
  const groups = groupsQuery.data ?? [];
  const attachedGroupIds = groups.map((g) => g.id);

  return (
    <div className="space-y-6">
      {groupsQuery.isLoading && (
        <p className="text-sm text-mp-muted">Loading security groups...</p>
      )}

      {groupsQuery.isError && (
        <Alert variant="error">
          <span>Failed to load security groups.</span>
        </Alert>
      )}

      {!groupsQuery.isLoading && !groupsQuery.isError && (
        <DealerSecurityGroupDataTable
          data={groups}
          emptyMessage="This dealer is not in any security groups."
          backRef={
            {
              path: `/admin/dealers/${dealerId}?tab=security-groups`,
              label: dealerName,
            } satisfies BackRef
          }
        />
      )}

      <div className="border-t border-mp-border pt-6 max-w-2xl">
        {attaching ? (
          <AttachDealerToGroupPanel
            dealerId={dealerId}
            dealerName={dealerName}
            attachedGroupIds={attachedGroupIds}
            onClose={() => setAttaching(false)}
          />
        ) : (
          <div>
            <h3 className="font-semibold mb-2">Attach to Security Group</h3>
            <div className="flex items-start gap-4 justify-between">
              <p className="text-sm text-mp-muted">
                Add this dealer to a security group to grant its users access to the group&apos;s products.
              </p>
              <PermissionGuard permission={{ resource: 'security_group', action: 'manage_dealers' }}>
                <Button size="sm" variant="primary" onClick={() => setAttaching(true)} className="whitespace-nowrap">
                  Attach to Security Group
                </Button>
              </PermissionGuard>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
